import React, {useEffect, useState, lazy, Suspense} from 'react';
import {useNavigate} from "react-router-dom";
import {Button, Card, Col, Modal, Row, Spin} from "antd";
import {CheckCircleFilled, RocketFilled} from "@ant-design/icons";
import api from "../../api";
import {useAuth} from "../../auth";
import {useTranslations} from "../../lang";
import {DEFAULT_GUTTER} from "../../constants";
import Footer from "../../Components/Footer";
import SEOHead from "../../Components/SEOHead";
import './style.css';
import {formatPlanPrice, setUtmData} from "../../helpers";
import {getMarketForLocation} from "../../environment";

// Lazy load below-fold components for better performance
const Stats = lazy(() => import("../../Components/Stats"));
const Features = lazy(() => import("../../Components/Features"));
const Reviews = lazy(() => import("../../Components/Reviews"));
const Plans = lazy(() => import("../../Components/Plans"));
const About = lazy(() => import("../../Components/About"));
const CallToAction = lazy(() => import("../../Components/CallToAction"));

// Loading fallback component for Suspense
const SectionLoader = () => (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <Spin size="large" />
    </div>
);

export default function Index() {

    const navigate = useNavigate();
    const {isApp} = useAuth();
    const {translate: t} = useTranslations();
    const [plans, setPlans] = useState([]);
    const {language} = useTranslations();
    const [videoModalOpen, setVideoModalOpen] = useState(false);
    const requestedMarket = getMarketForLocation(window.location.hostname, window.location.search);

    // Get suppliers from SSR if available
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        // if (api.isLoggedIn()) {
        //     navigate("/gardens");
        //     return;
        // }
        if (isApp) {
            navigate('/login');
            return;
        }
        setUtmData();

        api.getPublicSuppliers(language).then(setSuppliers)

    }, [isApp, navigate, language]);

    useEffect(() => {
        // Fetch plans from backend
        api.getAllPlans(requestedMarket).then(backendPlans => {
            // Filter only active plans for the index page
            const activePlans = backendPlans.filter(plan => plan.is_active);
            setPlans(activePlans);
        });
    }, [requestedMarket]);

    // Transform backend plans to match the expected format for the landing page
    const formatPlansForLanding = (backendPlans) => {
        return backendPlans.map(plan => {
            const features = [
                t("Onbeperkte tuinen"),
                t("Onbeperkte bedden per tuin")
            ];

            // Add crop limit info
            if (plan.crop_limit && plan.crop_limit > 0) {
                features.push(t(`Max ${plan.crop_limit} gewassen`));
            } else {
                features.push(t("Onbeperkte gewassen"));
            }

            // Add standard features
            features.push(t("Rij- en plantafstand calculator"));
            features.push(t("Zaai-, verplant- en oogsttijd calculator"));
            features.push(t("Toegang tot de grootste zaden database"));

            if (plan.has_team) {
                features.push(t("Onbeperkte teamleden"));
                features.push(t("Taakverdeling onder teamleden"));
            }
            if (plan.has_selling) {
                features.push(t('Verkoop via eigen pagina'));
            }

            return {
                id: plan.slug || plan.id,
                name: plan.name,
                price: plan.price === 0 ? t("Gratis") : plan.price === -1 ? t("Op aanvraag") : formatPlanPrice(plan.price, plan.currency),
                priceSubtext: plan.price > 0 ? t("per jaar") : null,
                buttonText: plan.price === 0 ? t("Start nu gratis") : t("Kies dit pakket"),
                onClick: () => navigate(plan.price < 0
                    ? '/register'
                    : `/register?plan_id=${plan.id}&market=${plan.market}`),
                featured: plan.is_featured || false,
                features
            };
        });
    };

    const formattedPlans = formatPlansForLanding(plans);

    return (
        <>
            <SEOHead
                title={t('Oogst.app - De motor achter een gestroomlijnde moestuin | Gratis Moestuin Planner')}
                description={t('Plan je moestuin, track je gewassen, en ontdek lokale aanbieders. Weet wanneer en hoeveel je moet zaaien. Gratis te gebruiken!')}
                keywords={t('moestuin, tuinplanner, gewassen, zaaikalender, oogsten, biologisch, lokaal, tuinieren, plantplanner, Nederland')}
                ogImage="https://oogst.app/static/img/og-image.jpg"
                ogImageAlt={t('De motor achter een gestroomlijnde moestuin')}
            />
            <div className="landing-container">
                {/* Skip to content link for keyboard navigation */}
                <a href="#main-content" className="skip-to-content">
                    {t("Spring naar hoofdinhoud")}
                </a>
                <main id="main-content">
                    {/* Hero Section */}
                    <section aria-labelledby="hero-title">
                        <Row gutter={DEFAULT_GUTTER} className="text-center mar-b-2">
                            <Col span={24}>
                                <Card className="hero-card mar-b-1">
                                    <div>
                                        <h1 id="hero-title" className="hero-title">
                                            {t("De motor achter een gestroomlijnde moestuin")}
                                        </h1>
                                        <p className="hero-subtitle">
                                            {t("Plan je moestuin, track je gewassen, en ontdek lokale aanbieders")}
                                        </p>
                                        <Row>
                                            <Col xs={24} md={12} className={"pad-t-2"}>
                                                <p className="hero-checklist-item text-left">
                                                    <CheckCircleFilled className="hero-checklist-icon" aria-hidden="true"/>
                                                    {t("Weet precies wanneer en hoeveel je moet zaaien")}
                                                </p>
                                                <p className="hero-checklist-item text-left">
                                                    <CheckCircleFilled className="hero-checklist-icon" aria-hidden="true"/>
                                                    {t("Nooit meer een leeg bed of mislukte oogst")}
                                                </p>
                                                <p className="hero-checklist-item text-left">
                                                    <CheckCircleFilled className="hero-checklist-icon" aria-hidden="true"/>
                                                    {t("Altijd je planning in je broekzak")}
                                                </p>
                                                <p className="hero-checklist-item text-left">
                                                    <CheckCircleFilled className="hero-checklist-icon" aria-hidden="true"/>
                                                    {t("Toegang tot de grootste zaden database")}
                                                </p>
                                                <p className="hero-checklist-item text-left">
                                                    <CheckCircleFilled className="hero-checklist-icon" aria-hidden="true"/>
                                                    {t("Gratis toegang tot onze community voor vragen en tips")}
                                                </p>
                                            </Col>
                                            <Col xs={24} md={12} className={"pad-2"}>
                                                <button
                                                    onClick={() => setVideoModalOpen(true)}
                                                    style={{border: 'none', background: 'none', padding: 0, cursor: 'pointer', width: '100%'}}
                                                    aria-label={t("Bekijk de introductievideo van Oogst.app")}
                                                >
                                                    <img src={"static/img/vid-placeholder.png"}
                                                         alt={t("Bekijk de introductievideo van Oogst.app")}
                                                         fetchPriority="high"
                                                         className={"m-width-100"}/>
                                                </button>
                                                <Modal width={"90%"} open={videoModalOpen} footer={null} onCancel={() => setVideoModalOpen(false)}>
                                                    {videoModalOpen && (
                                                        <iframe width="100%" height="500" className={"mar-t-2"}
                                                                src="https://www.youtube-nocookie.com/embed/oIkpffIyvc8"
                                                                title={t("YouTube video speler - Oogst.app introductie")}
                                                                frameBorder="0"
                                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                referrerPolicy="strict-origin-when-cross-origin"
                                                                allowFullScreen></iframe>
                                                    )}
                                                </Modal>
                                            </Col>
                                        </Row>
                                        <div className="hero-cta-wrapper">
                                            <Button
                                                onClick={() => navigate('/register')}
                                                type="primary"
                                                size="large"
                                                className="btn-hero"
                                                icon={<RocketFilled aria-hidden="true"/>}
                                            >
                                                {t("Start nu gratis")}
                                            </Button>
                                        </div>
                                        <div className="hero-footer-text">
                                            <p>
                                                ✨ {t("Gratis voor altijd • Geen verborgen kosten • 30 dagen geld terug garantie")}
                                            </p>
                                        </div>
                                    </div>
                                </Card>
                            </Col>
                        </Row>
                    </section>


                    {/* Statistics */}
                    <section aria-labelledby="stats-title">
                        <div className="section-header-center">
                            <h2 id="stats-title" className="section-title">
                                {t("Vertrouwd door duizenden tuinders")}
                            </h2>
                            <p>
                                {t("Sluit je aan bij de groeiende gemeenschap van slimme tuinders")}
                            </p>
                        </div>
                        <div className={"pad-1"}>
                            <Suspense fallback={<SectionLoader />}>
                                <Stats/>
                            </Suspense>
                        </div>

                    </section>

                    {/* Suppliers */}
                    <section aria-labelledby="suppliers-title" className={"pad-1"}>
                        <div className="section-header-center">
                            <h2 id="suppliers-title" className="section-title">
                                {t("Gewasinformatie van van onder andere")}
                            </h2>
                            <p>{t('Iedere week lezen we alle infromatie opnieuw in')}</p>
                            {suppliers.length > 0 && (
                                <div className="supplier-logo-row mar-t-2">
                                    {suppliers
                                        .filter((supplier, index, self) =>
                                            supplier.website && self.findIndex(s => s.website === supplier.website) === index
                                        )
                                        .map(supplier => (
                                            <img
                                                key={supplier.id}
                                                src={supplier.logo}
                                                alt={`${supplier.name} ${t("logo")}`}
                                                loading="lazy"
                                                className="supplier-logo"
                                            />
                                        ))}
                                </div>
                            )}
                        </div>

                    </section>

                    {/* Features Section */}
                    <section aria-labelledby="features-title" className={"pad-2"}>
                        <div className="section-header-center">
                            <h2 id="features-title" className="section-title">
                                {t("Waar we mee helpen")}
                            </h2>
                        </div>
                        <Suspense fallback={<SectionLoader />}>
                            <Features/>
                        </Suspense>
                    </section>

                    {/* Reviews Section */}
                    <section aria-labelledby="reviews-title" className={"pad-2"}>
                        <div className="section-header-center">
                            <h2 id="reviews-title" className="section-title">
                                {t("Reviews: Wat onze gebruikers zeggen")}
                            </h2>
                        </div>
                        <Suspense fallback={<SectionLoader />}>
                            <Reviews/>
                        </Suspense>
                    </section>

                    {/* Pricing Section */}
                    <section aria-labelledby="pricing-title" className={"pad-2"}>
                        <Row gutter={DEFAULT_GUTTER} className="mar-b-2">
                            <Col span={24}>
                                <div className="section-header-center mar-y-3">
                                    <h2 id="pricing-title" className="section-title">
                                        {t("Kies jouw moestuin plan")}
                                    </h2>
                                    <p className="section-subtitle">
                                        {t("Start gratis, upgrade wanneer je wilt. Geen verrassingen.")}
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Suspense fallback={<SectionLoader />}>
                            <Plans plans={formattedPlans}/>
                        </Suspense>
                    </section>

                    {/* About Founder Section */}
                    <section aria-labelledby="about-title" className={"pad-2"}>
                        <Suspense fallback={<SectionLoader />}>
                            <About/>
                        </Suspense>
                    </section>

                    {/* Call to Action */}
                    <Suspense fallback={<SectionLoader />}>
                        <CallToAction showLogin={true} showHomepageLink={false} />
                    </Suspense>
                </main>
                <Footer/>
            </div>
        </>
    )
}
